<template>
  <div>
    <div v-if="initLoading" class="text-xs-center pt-5">
      <v-progress-circular :size="70" :width="7" color="secondary" indeterminate />
    </div>
    <v-container v-else class="pt-5 pb-7">
      <v-layout row wrap justify-content-center>
        <v-flex>
          <alert-banner :msg="alertMessage" />
        </v-flex>
      </v-layout>
      <v-form @submit.prevent="saveMetadata">
        <v-layout row wrap>
          <v-flex md6>
            <v-card flat>
              <v-text-field v-model="editForm.title" label="Course Title" box />

              <v-autocomplete v-if="isAdmin"
                              v-model="editForm.userId"
                              v-validate="'required'"
                              :items="authors"
                              label="Author"
                              name="author"
                              box
                              cache-items
                              clearable
                              item-value="id"
                              item-text="fullName"
                              placeholder="Start typing to Search"
                              :search-input.sync="search"
                              :error-messages="errors.collect('author')"
                              :loading="loadingAutocomplete"
              >
                <template v-slot:selection="data">
                  {{ data.item.fullName }} ({{ data.item.email }})
                </template>
                <template v-slot:item="data">
                  <v-list-tile-avatar>
                    <img :src="data.item.profileImageUrl">
                  </v-list-tile-avatar>
                  <v-list-tile-content>
                    <v-list-tile-title v-html="data.item.fullName" />
                    <v-list-tile-sub-title v-html="data.item.email" />
                  </v-list-tile-content>
                </template>
              </v-autocomplete>

              <v-textarea v-model="editForm.description" label="Description" box />

              <v-autocomplete v-model="editForm.courseGradeLevels" box :items="gradeLevels" label="Grade Levels" item-text="name" return-object multiple chips>
                <template v-slot:selection="data">
                  <v-chip :selected="data.selected" close class="chip--select-multi" dark @input="editForm.courseGradeLevels.splice(data.item, 1)">
                    <v-avatar v-if="data.item.iconUrl">
                      <img :src="data.item.iconUrl">
                    </v-avatar>
                    {{ data.item.name }}
                  </v-chip>
                </template>
              </v-autocomplete>

              <v-autocomplete v-model="editForm.coursePrograms" box :items="programs" label="Programs" item-text="name" item-value="id" return-object multiple chips>
                <template v-slot:selection="data">
                  <v-chip :selected="data.selected" close class="chip--select-multi" dark @input="editForm.coursePrograms.splice(data.item, 1)">
                    <v-avatar v-if="data.item.iconUrl">
                      <img :src="data.item.iconUrl">
                    </v-avatar>
                    {{ data.item.name }}
                  </v-chip>
                </template>
              </v-autocomplete>

              <v-autocomplete v-model="editForm.courseResourceTypes" box :items="resourceTypes" label="Resource Types" item-text="name" return-object multiple chips>
                <template v-slot:selection="data">
                  <v-chip :selected="data.selected" close class="chip--select-multi" dark @input="editForm.courseResourceTypes.splice(data.item, 1)">
                    <v-avatar v-if="data.item.iconUrl">
                      <img :src="data.item.iconUrl">
                    </v-avatar>
                    {{ data.item.name }}
                  </v-chip>
                </template>
              </v-autocomplete>

              <v-select v-model="editForm.length" label="Course Length" :items="courseLengths" clearable box />

              <v-text-field v-model="editForm.ksdeCourseNumber" label="KSDE Course No." box />

              <div class="text-xs-right">
                <v-btn color="primary" flat @click="editForm = JSON.parse(JSON.stringify(metadata))">
                  Revert Changes
                </v-btn>
                <v-btn color="secondary" :loading="savingMetadata" type="submit">
                  Save Changes
                </v-btn>
              </div>
            </v-card>
          </v-flex>
          <v-flex v-if="isAdmin" md1 class="text-xs-center">
            <v-divider vertical class="ma-0" />
          </v-flex>
          <v-flex v-if="isAdmin" md5>
            <v-card flat>
              <v-checkbox v-model="editForm.active" label="Active Course" />

              <v-menu v-model="lastUpdatedDateMenu" :close-on-content-click="false" :nudge-right="40" lazy transition="scale-transition" offset-y full-width min-width="290px">
                <template v-slot:activator="{ on }">
                  <v-text-field v-model="lastUpdatedDateFormatted" label="Last Updated Date" box @blur="editForm.lastCourseUpdateDate = parseDate(lastUpdatedDateFormatted)" v-on="on" />
                </template>
                <v-date-picker v-model="editForm.lastCourseUpdateDate" header-color="secondary" color="secondary" @input="lastUpdatedDateMenu = false" />
              </v-menu>


              <v-card class="mt-5">
                <v-textarea v-model="noteForm.note" label="Internal Notes (admins only)" box />
                <v-btn color="secondary" :loading="updatingNotes" @click.native="saveInternalNote">
                  Update Notes
                </v-btn>
              </v-card>

              <v-card class="mt-5">
                <v-toolbar color="white" flat>
                  <v-toolbar-title>Course Tags</v-toolbar-title>
                  <v-spacer />
                  <v-dialog v-model="newTagDialog" max-width="500px">
                    <v-btn slot="activator" color="secondary" dark class="mb-2" icon round>
                      <v-icon>add</v-icon>
                    </v-btn>
                    <form @submit.prevent="saveNewTag">
                      <v-card>
                        <v-card-title>
                          <span class="headline">New Tag</span>
                        </v-card-title>

                        <v-card-text>
                          <v-container grid-list-md>
                            <v-layout wrap>
                              <v-flex xs12>
                                <v-autocomplete v-model="newTagObject" required :items="tags" return-object item-text="name" label="Select a Tag Name">
                                  <template slot="item" slot-scope="{ item, tile }">
                                    <v-list-tile-content>
                                      <v-list-tile-title>{{ item.name }}</v-list-tile-title>
                                    </v-list-tile-content>
                                  </template>
                                </v-autocomplete>
                              </v-flex>
                            </v-layout>
                          </v-container>
                        </v-card-text>

                        <v-card-actions>
                          <v-spacer />
                          <v-btn color="blue darken-1" flat @click.native="closeNewTagModal">
                            Cancel
                          </v-btn>
                          <v-btn color="blue darken-1" flat type="submit" :loading="savingNewTag">
                            Save
                          </v-btn>
                        </v-card-actions>
                      </v-card>
                    </form>
                  </v-dialog>
                </v-toolbar>
                <v-data-table flat :headers="[{ text: 'Tags', value: 'name', align: 'left' },{align: 'right', sortable: false,}]" :items="courseTags" :loading="fetchingTags">
                  <template v-slot:items="props">
                    <td>{{ props.item.name }}</td>
                    <td class="text-xs-right">
                      <v-icon @click="deleteTag(props.item)">
                        far fa-trash-alt
                      </v-icon>
                    </td>
                  </template>
                </v-data-table>
              </v-card>

              <author-users-card :course-id="courseId" />
            </v-card>
          </v-flex>
        </v-layout>
      </v-form>
    </v-container>
  </div>
</template>
<script>
  import axios from 'axios';
  import _ from 'lodash';
  import ManageCourseBase from './ManageCourseBase.vue';
  import AuthorUsersCard from './AuthorUsersCard.vue';

  export default {
    components: {
      AuthorUsersCard,
    },
    extends: ManageCourseBase,

    data: vm => ({
      loadingAutocomplete: false,
      search: null,
      initLoading: true,
      courseLengths: ['1 Semester', '2 Semesters'],
      tabs: ['Metadata', 'Files', 'Organization', 'Workflow'],
      selectedTab: 'Metadata',
      editForm: {
        id: null,
        title: '',
        description: '',
        active: false,
        lastCourseUpdateDate: new Date().toISOString().substr(0, 10),
        userId: null,
        courseGradeLevels: [],
        coursePrograms: [],
        courseResourceTypes: [],
        internalNotes: '',
      },
      noteForm: {
        note: '',
      },
      lastUpdatedDateFormatted: vm.formatDate(new Date().toISOString().substr(0, 10)),
      lastUpdatedDateMenu: false,
      newTagObject: null,
      metadata: null,
      savingMetadata: false,
      updatingNotes: false,
      newTagDialog: false,
      fetchingTags: false,
      savingNewTag: false,
      tags: [],
      authors: [],
      courseTags: [],
    }),

    watch: {
      search(val) {
        this.loadingAutocomplete = true;
        this.processSearch();
      },
      'editForm.lastCourseUpdateDate': function (val) {
        this.lastUpdatedDateFormatted = this.formatDate(val);
      },
    },

    mounted() {
      this.getMetadata().then(() => {
        this.initLoading = false;
        setTimeout(() => {
          this.search = this.metadata.user ? this.metadata.user.fullName : null;
        }, 100);
      });
      this.getTags();
      this.getCourseTags();
    },

    methods: {
      async getMetadata() {
        let { data } = await axios.get(`/api/courses/${this.courseId}`);
        data = await this.morphReturnData(data);
        this.metadata = data;
        this.editForm = JSON.parse(JSON.stringify(data));
        this.noteForm.note = JSON.parse(JSON.stringify(data)).internalNotes;
      },

      async getTags() {
        const { data } = await axios.get('/api/tagnames?pageSize=99999&currentPage=1');
        this.tags = data.resultItems;
      },

      async getUsers() {
        const { data } = await axios.get(`/api/users/dropdown?filter=${this.search}&currentPage=0&pageSize=20`);
        this.authors = data.resultItems;
      },

      async getCourseTags() {
        this.fetchingTags = true;
        const { data } = await axios.get(`/api/courses/${this.courseId}/tagnames`);
        this.courseTags = data;
        this.fetchingTags = false;
      },

      async saveMetadata() {
        this.savingMetadata = true;
        this.editForm.internalNotes = this.noteForm.note;
        try {
          const clonedForm = JSON.parse(JSON.stringify(this.editForm));
          delete clonedForm.courseAuthorTeams;
          await axios.patch(`/api/courses/${this.courseId}`, clonedForm);
          await axios.put(`/api/courses/${this.courseId}/programs`, this.editForm.coursePrograms);
          await axios.put(`/api/courses/${this.courseId}/gradelevels`, this.editForm.courseGradeLevels);
          await axios.put(`/api/courses/${this.courseId}/resourcetypes`, this.editForm.courseResourceTypes);
          this.metadata = JSON.parse(JSON.stringify(this.editForm));
        } catch (error) {
          this.processErrors(error, this.editForm);
        } finally {
          this.savingMetadata = false;
        }
      },
      async saveInternalNote() {
        this.updatingNotes = true;
        try {
          await axios.patch(`/api/courses/${this.courseId}/note`, this.noteForm);
        } catch (error) {
          this.processErrors(error, this.editForm);
        } finally {
          this.updatingNotes = false;
        }
      },
      closeNewTagModal() {
        this.newTagDialog = false;
        setTimeout(() => {
          this.newTagObject = null;
        }, 300);
      },

      async saveNewTag(addAnother = false) {
        this.savingNewTag = true;
        await axios.post(`/api/courses/${this.courseId}/tagnames`, this.newTagObject);
        if (addAnother) {
          this.closeNewTagModal();
        }
        await this.getCourseTags();
        this.savingNewTag = false;
      },

      async deleteTag(tag) {
        const result = confirm('Are you sure you want to delete this item?'); // eslint-disable-line
        if (result) {
          this.courseTags.filter(i => i.id !== tag.id);
          await axios.delete(`/api/courses/${this.courseId}/tagnames/${tag.id}`);
          await this.getCourseTags();
        }
      },

      processSearch: _.debounce(function (e) {
        this.getUsers().then(() => {
          this.loadingAutocomplete = false;
        });
      }, 600),

      morphReturnData(data) {
        data.courseGradeLevels.forEach((grade) => {
          const result = this.gradeLevels.find(level => level.id === grade.gradeLevelId);
          if (result) {
            grade.id = grade.gradeLevelId;
            grade.iconUrl = result.iconUrl;
            grade.name = result.name;
            delete grade.gradeLevel;
            delete grade.gradeLevelId;
            delete grade.courseId;
            delete grade.createdAt;
            delete grade.updatedAt;
          }
        });

        data.coursePrograms.forEach((program) => {
          const result = this.programs.find(level => level.id === program.programId);
          if (result) {
            program.id = program.programId;
            program.iconUrl = result.iconUrl;
            program.name = result.name;
            delete program.program;
            delete program.programId;
            delete program.courseId;
            delete program.createdAt;
            delete program.updatedAt;
          }
        });

        data.courseResourceTypes.forEach((type) => {
          const result = this.resourceTypes.find(level => level.id === type.resourceTypeId);
          if (result) {
            type.id = type.resourceTypeId;
            type.iconUrl = result.iconUrl;
            type.name = result.name;
            delete type.resourceType;
            delete type.resourceTypeId;
            delete type.courseId;
            delete type.createdAt;
            delete type.updatedAt;
          }
        });
        return data;
      },
    },
  };
</script>